import React, { lazy } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  Outlet,
  useParams,
  useLocation,
} from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { datePickerAdapterLocale } from '@/shared/ui/form/datePickerLocale';
import { ThemeProvider } from '@/shared/context/ThemeContext';
import { SnackbarProvider } from '@/shared/hooks/useSnackbar';
import { ClinicScopeProvider } from '@/shared/context/ClinicScopeContext';
import { QueryProvider } from '@/shared/query';
import { MasterDataSync } from '@/shared/query/MasterDataSync';
import { ConfirmProvider } from '@/shared/confirm';

// Layout + auth (eager — first paint / login)
import Layout from '@/shared/layout/Layout';
import Login from '@/modules/identity/pages/Login';
import ForgotPassword from '@/modules/identity/pages/ForgotPassword';
import ResetPassword from '@/modules/identity/pages/ResetPassword';
import ChangePassword from '@/modules/identity/pages/ChangePassword';
import NotFound from '@/shared/components/errors/NotFound';
import RouteErrorPage from '@/shared/components/errors/RouteErrorPage';
import LegacyCreateTreatmentPlanRedirect from '@/modules/patient/pages/PatientDetails/LegacyCreateTreatmentPlanRedirect';
import UnsupportedResolution from '@/shared/components/errors/UnsupportedResolution';

// Context + routing
import { AuthProvider, useAuth } from '@/shared/context/AuthContext';
import { OrgSettingsProvider, useOrgSettings } from '@/shared/context/OrgSettingsContext';
import PrivateRoute from '@/shared/routing/PrivateRoute';
import { RealtimeConnectionProvider } from '@/shared/realtime';
import SuperAdminRoute from '@/shared/routing/SuperAdminRoute';
import OrgAppRoute from '@/shared/routing/OrgAppRoute';
import PlanFeatureGate from '@/shared/routing/PlanFeatureGate';
import OrganizationCheck from '@/shared/routing/OrganizationCheck';
import IdleLogoutWatcher from '@/shared/routing/IdleLogoutWatcher';
import RootRedirect from '@/shared/routing/RootRedirect';
import { useAppViewportMatch } from '@/shared/hooks/useAppViewportMatch';
import { isViewportExemptPath } from '@/shared/utils/viewportSettings';

// Authenticated pages (lazy — code-split by route)
const Dashboard = lazy(() => import('@/modules/reports/pages/Dashboard'));
const Patients = lazy(() => import('@/modules/patient/pages/Patients'));
const PatientFormPage = lazy(() => import('@/modules/patient/pages/PatientFormPage'));
const PatientDetails = lazy(() => import('@/modules/patient/pages/PatientDetails'));
const Leads = lazy(() => import('@/modules/lead/pages/Leads'));
const EditLead = lazy(() => import('@/modules/lead/pages/EditLead'));
const LeadDetails = lazy(() => import('@/modules/lead/pages/LeadDetails'));
const LeadReports = lazy(() => import('@/modules/lead/pages/LeadReports'));
const MessagingInbox = lazy(() => import('@/modules/messaging/pages/InboxPage'));
const InternalChatInbox = lazy(() => import('@/modules/internal-chat/pages/InboxPage'));
const MessagingCannedResponses = lazy(
  () => import('@/modules/messaging/pages/CannedResponsesPage')
);
const RevenueReport = lazy(() => import('@/modules/reports/pages/RevenueReport'));
const PatientsReport = lazy(() => import('@/modules/reports/pages/PatientsReport'));
const ReferrerHub = lazy(() => import('@/modules/referrer/pages/ReferrerHub'));
const ReferralCommissionHub = lazy(() => import('@/modules/referrer/pages/ReferralCommissionHub'));
const ReferrersReport = lazy(() => import('@/modules/reports/pages/ReferrersReport'));
const AppointmentsReport = lazy(() => import('@/modules/reports/pages/AppointmentsReport'));
const TreatmentsReport = lazy(() => import('@/modules/reports/pages/TreatmentsReport'));
const Appointments = lazy(() => import('@/modules/appointment/pages/Appointments'));
const AppointmentDetailRedirect = lazy(() => import('@/modules/appointment/pages/AppointmentDetailRedirect'));
const AppointmentTypes = lazy(() => import('@/modules/appointment/pages/AppointmentTypes'));
const CustomerSources = lazy(() => import('@/modules/patient/pages/CustomerSources'));
const DiagnosisCodes = lazy(() => import('@/modules/diagnosis/pages/DiagnosisCodes'));
const DiagnosisBlocks = lazy(() => import('@/modules/diagnosis/pages/DiagnosisBlocks'));
const TreatmentCatalog = lazy(() => import('@/modules/treatment-catalog/pages/TreatmentCatalog'));
const TreatmentCategories = lazy(
  () => import('@/modules/treatment-catalog/pages/TreatmentCategories')
);
const TreatmentUoms = lazy(() => import('@/modules/treatment-catalog/pages/TreatmentUoms'));
const TreatmentJournals = lazy(() => import('@/modules/treatment-journal/pages/TreatmentJournals'));
const EditTreatmentCatalogItem = lazy(
  () => import('@/modules/treatment-catalog/pages/EditTreatmentCatalogItem')
);
const TreatmentPlans = lazy(() => import('@/modules/treatment-plan/pages/TreatmentPlans'));
const TreatmentPlanDetails = lazy(
  () => import('@/modules/treatment-plan/pages/TreatmentPlanDetails')
);
const EditTreatmentPlan = lazy(() => import('@/modules/treatment-plan/pages/EditTreatmentPlan'));
const TreatmentSessions = lazy(() => import('@/modules/treatment-session/pages/TreatmentSessions'));
const AddTreatmentSession = lazy(
  () => import('@/modules/treatment-session/pages/AddTreatmentSession')
);
const EditTreatmentSession = lazy(
  () => import('@/modules/treatment-session/pages/EditTreatmentSession')
);
const Payments = lazy(() => import('@/modules/payment/pages/Payments'));
const Expenses = lazy(() => import('@/modules/expense/pages/Expenses'));
const ExpenseCategories = lazy(() => import('@/modules/expense/pages/ExpenseCategories'));
const PaymentMethods = lazy(() => import('@/modules/payment/pages/PaymentMethods'));
const Incomes = lazy(() => import('@/modules/income/pages/Incomes'));
const IncomeCategories = lazy(() => import('@/modules/income/pages/IncomeCategories'));
const Promotions = lazy(() => import('@/modules/promotion/pages/Promotions'));
const CommissionHub = lazy(() => import('@/modules/commission/pages/CommissionHub'));
const PayrollHub = lazy(() => import('@/modules/payroll/pages/PayrollHub'));
const PayrollPeriodDetails = lazy(() => import('@/modules/payroll/pages/PayrollPeriodDetails'));
const Cashbook = lazy(() => import('@/modules/finance/pages/Cashbook'));
const WarehouseConfigHub = lazy(() => import('@/modules/inventory/pages/WarehouseConfigHub'));
const StockInList = lazy(() => import('@/modules/inventory/stock-in/pages/StockInList'));
const StockOutList = lazy(() => import('@/modules/inventory/stock-out/pages/StockOutList'));
const TransferList = lazy(() => import('@/modules/inventory/transfer/pages/TransferList'));
const StocktakeList = lazy(() => import('@/modules/inventory/stocktake/pages/StocktakeList'));
const InventoryReports = lazy(() => import('@/modules/inventory/reports/pages/InventoryReports'));
const LabPartnerEditRedirect = lazy(
  () => import('@/modules/labo/partners/pages/LabPartnerEditRedirect')
);
const LabOrders = lazy(() => import('@/modules/labo/orders/pages/LabOrders'));
const CreateLabOrder = lazy(() => import('@/modules/labo/orders/pages/CreateLabOrder'));
const LabOrderIdRedirect = lazy(() => import('@/modules/labo/orders/pages/LabOrderIdRedirect'));
const LaboMasterHub = lazy(() => import('@/modules/labo/pages/LaboMasterHub'));
const Clinics = lazy(() => import('@/modules/clinic/pages/Clinics'));
const Staff = lazy(() => import('@/modules/staff/pages/Staff'));
const Departments = lazy(() => import('@/modules/staff/pages/Departments'));
const StaffDetails = lazy(() => import('@/modules/staff/pages/StaffDetails'));
const AddStaff = lazy(() => import('@/modules/staff/pages/AddStaff'));
const EditStaff = lazy(() => import('@/modules/staff/pages/EditStaff'));
const TimeOffHub = lazy(() => import('@/modules/scheduling/time-off/pages/TimeOffHub'));
const TimeOffDetailRedirect = lazy(
  () => import('@/modules/scheduling/time-off/pages/TimeOffDetailRedirect'),
);
const ApplyWeeklyTemplateRedirect = lazy(
  () => import('@/modules/scheduling/assignments/pages/ApplyWeeklyTemplateRedirect')
);
const TeamScheduleHub = lazy(
  () => import('@/modules/scheduling/calendar/pages/TeamScheduleHub')
);
const AttendanceHub = lazy(() => import('@/modules/attendance/pages/AttendanceHub'));
const OvertimeHub = lazy(() => import('@/modules/overtime/pages/OvertimeHub'));
const AttendanceClock = lazy(() => import('@/modules/attendance/pages/AttendanceClock'));
const AttendanceDetails = lazy(() => import('@/modules/attendance/pages/AttendanceDetails'));
const WorkTimesheetPage = lazy(
  () => import('@/modules/work-timesheet/pages/WorkTimesheetPage')
);
const MeHome = lazy(() => import('@/modules/me/pages/MeHome'));
const Tasks = lazy(() => import('@/modules/task/pages/Tasks'));
const AddTask = lazy(() => import('@/modules/task/pages/AddTask'));
const EditTask = lazy(() => import('@/modules/task/pages/EditTask'));
const TaskDetails = lazy(() => import('@/modules/task/pages/TaskDetails'));
const Projects = lazy(() => import('@/modules/project/pages/Projects'));
const KpiHub = lazy(() => import('@/modules/kpi/pages/KpiHub'));
const KpiCycleDetails = lazy(() => import('@/modules/kpi/pages/KpiCycleDetails'));
const Notifications = lazy(() => import('@/modules/notification/pages/Notifications'));
const Settings = lazy(() => import('@/modules/settings/pages/Settings'));
const OutboundChannels = lazy(() => import('@/modules/settings/pages/OutboundChannels'));
const Automation = lazy(() => import('@/modules/automation/pages/Automation'));
const AutomationRuleEditor = lazy(
  () => import('@/modules/automation/pages/AutomationRuleEditor')
);
const Profile = lazy(() => import('@/modules/identity/pages/Profile'));
const PlatformOrganizations = lazy(() => import('@/modules/platform/pages/PlatformOrganizations'));
const ProvisionOrganization = lazy(() => import('@/modules/platform/pages/ProvisionOrganization'));
const Users = lazy(() => import('@/modules/identity/pages/Users'));
const PermissionGroups = lazy(() => import('@/modules/identity/pages/PermissionGroups'));
const AuditEvents = lazy(() => import('@/modules/audit/pages/AuditEvents'));
const Drugs = lazy(() => import('@/modules/prescription/pages/Drugs'));
const PrescriptionTemplates = lazy(
  () => import('@/modules/prescription/pages/PrescriptionTemplates')
);
const Prescriptions = lazy(() => import('@/modules/prescription/pages/Prescriptions'));

console.log('🔍 Frontend Debug Info:');
console.log('   VITE_API_URL:', import.meta.env.VITE_API_URL);
console.log('   NODE_ENV:', import.meta.env.MODE);
console.log('   Current URL:', window.location.href);

/** Old `/kpi/sheets/:id` → hub Phiếu KPI tab (drawer is local state, not URL). */
function LegacyKpiSheetRedirect(): React.JSX.Element {
  return <Navigate to="/kpi/cycles?tab=sheets" replace />;
}

function LegacyTreatmentDetailRedirect(): React.JSX.Element {
  const { id } = useParams();
  return <Navigate to={`/treatment-catalog/${id}/edit`} replace />;
}

function LegacyTreatmentEditRedirect(): React.JSX.Element {
  const { id } = useParams();
  return <Navigate to={`/treatment-catalog/${id}/edit`} replace />;
}

function TreatmentCatalogDetailRedirect(): React.JSX.Element {
  const { id } = useParams();
  return <Navigate to={`/treatment-catalog/${id}/edit`} replace />;
}

/** Old full-page create-prescription URLs open the chart tab and the create dialog. */
function LegacyPatientPrescriptionNewRedirect(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  return (
    <Navigate
      to={`/patients/${id}?tab=prescriptions`}
      replace
      state={{ createPrescription: true }}
    />
  );
}

/** Old `/patients/:id/payments/new` opens the chart payments tab and the record dialog. */
function LegacyPatientPaymentNewRedirect(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  return (
    <Navigate
      to={`/patients/${id}?tab=payments`}
      replace
      state={{ recordPayment: true }}
    />
  );
}

/** Old `/patients/:id/lab-orders/new` opens the chart lab-orders tab and the create dialog. */
function LegacyPatientLabOrderNewRedirect(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  return (
    <Navigate
      to={`/patients/${id}?tab=lab-orders`}
      replace
      state={{ createLabOrder: true }}
    />
  );
}

/** Old `/treatment-sessions/:id` opens the list drawer for that visit. */
function TreatmentSessionDetailRedirect(): React.JSX.Element {
  const { id } = useParams();
  const target = id
    ? `/treatment-sessions?session=${encodeURIComponent(id)}`
    : '/treatment-sessions';
  return <Navigate to={target} replace />;
}

function CommissionRuleEditRedirect(): React.JSX.Element {
  return <Navigate to="/commissions?tab=rules" replace />;
}

/** Old full-page create stock-out URLs open the list dialog with the same query. */
function LegacyStockOutCreateRedirect(): React.JSX.Element {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/inventory/stock-out', search }} replace />;
}

function StockInReceiptRedirect(): React.JSX.Element {
  const { id } = useParams();
  const target = id
    ? `/inventory/stock-in?receipt=${encodeURIComponent(id)}`
    : '/inventory/stock-in';
  return <Navigate to={target} replace />;
}

function TransferIdRedirect(): React.JSX.Element {
  const { id } = useParams();
  if (id === 'create') {
    return <Navigate to="/inventory/transfers?create=1" replace />;
  }
  const target = id
    ? `/inventory/transfers?transfer=${encodeURIComponent(id)}`
    : '/inventory/transfers';
  return <Navigate to={target} replace />;
}

function StockOutIssueRedirect(): React.JSX.Element {
  const { id } = useParams();
  const target = id
    ? `/inventory/stock-out?issue=${encodeURIComponent(id)}`
    : '/inventory/stock-out';
  return <Navigate to={target} replace />;
}

function StocktakeIdRedirect(): React.JSX.Element {
  const { id } = useParams();
  if (id === 'create') {
    return <Navigate to="/inventory/stocktakes?create=1" replace />;
  }
  const target = id
    ? `/inventory/stocktakes?stocktake=${encodeURIComponent(id)}`
    : '/inventory/stocktakes';
  return <Navigate to={target} replace />;
}

function OrgAppShell(): React.JSX.Element {
  return (
    <PrivateRoute>
      <OrgAppRoute>
        <PlanFeatureGate>
          <OrganizationCheck>
            <RealtimeConnectionProvider>
              <Layout />
            </RealtimeConnectionProvider>
          </OrganizationCheck>
        </PlanFeatureGate>
      </OrgAppRoute>
    </PrivateRoute>
  );
}

/**
 * Providers live in the data-router tree (not only around RouterProvider)
 * so every route — including lazy pages and error UI — always sees OrgSettings.
 * Viewport gate skips auth/platform routes and unauthenticated sessions so login stays reachable.
 */
function AppViewportGate({ children }: { children: React.ReactNode }): React.JSX.Element {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { viewport, orgSettingsReady } = useOrgSettings();
  const matchesViewport = useAppViewportMatch(viewport.minWidth, viewport.minHeight);

  // Wait for org settings after login — defaults have enabled=true and would flash UnsupportedResolution.
  const skipGate =
    !isAuthenticated ||
    !orgSettingsReady ||
    isViewportExemptPath(location.pathname) ||
    !viewport.enabled;
  const allowed = skipGate || matchesViewport;

  return <>{allowed ? children : <UnsupportedResolution />}</>;
}

function AppProviders(): React.JSX.Element {
  return (
    <AuthProvider>
      <OrgSettingsProvider>
        <ThemeProvider>
          <AppViewportGate>
            <SnackbarProvider>
              <QueryProvider>
                <ConfirmProvider>
                  <IdleLogoutWatcher />
                  <ClinicScopeProvider>
                    <MasterDataSync />
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={datePickerAdapterLocale}
                    >
                      <Outlet />
                    </LocalizationProvider>
                  </ClinicScopeProvider>
                </ConfirmProvider>
              </QueryProvider>
            </SnackbarProvider>
          </AppViewportGate>
        </ThemeProvider>
      </OrgSettingsProvider>
    </AuthProvider>
  );
}

const PayrollEntryPageRedirect: React.FC = () => {
  const { id } = useParams();
  return <Navigate to={id ? `/payroll/${id}` : '/payroll'} replace />;
};

const ProjectLegacyRedirect: React.FC<{ edit?: boolean }> = ({ edit = false }) => {
  const { id } = useParams();
  if (!id) return <Navigate to="/projects?view=list" replace />;
  const params = new URLSearchParams({ view: 'list', id });
  if (edit) params.set('edit', '1');
  return <Navigate to={`/projects?${params.toString()}`} replace />;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppProviders />}>
      <Route element={<Outlet />} errorElement={<RouteErrorPage />}>
              <Route path="/" element={<RootRedirect />} />
              
              <Route path="/register" element={<Navigate to="/login" replace />} />
              <Route path="/organization-setup" element={<Navigate to="/login" replace />} />
              <Route path="/organization-choice" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:resetToken" element={<ResetPassword />} />
              <Route path="/change-password" element={
                <PrivateRoute>
                  <ChangePassword />
                </PrivateRoute>
              } />
              
              <Route path="/platform" element={
                <SuperAdminRoute>
                  <Layout />
                </SuperAdminRoute>
              }>
                <Route index element={<PlatformOrganizations />} />
                <Route path="provision" element={<ProvisionOrganization />} />
              </Route>

              <Route path="/dashboard" element={<OrgAppShell />}>
                <Route index element={<Dashboard />} />
              </Route>
              
              <Route path="/patients" element={
                <OrgAppShell />
              }>
                <Route element={<Patients />}>
                  <Route index element={null} />
                  <Route
                    path="add"
                    element={<Navigate to="/patients" replace state={{ openAddPatient: true }} />}
                  />
                </Route>
                <Route path=":id" element={<PatientDetails />}>
                  <Route path="edit" element={<PatientFormPage />} />
                  <Route path="treatment-plans/new" element={<LegacyCreateTreatmentPlanRedirect />} />
                  <Route path="treatment-plans/:planId/edit" element={<EditTreatmentPlan />} />
                  <Route path="treatment-plans/:planId" element={<EditTreatmentPlan />} />
                  <Route path="payments/new" element={<LegacyPatientPaymentNewRedirect />} />
                  <Route path="prescriptions/new" element={<LegacyPatientPrescriptionNewRedirect />} />
                  <Route path="lab-orders/new" element={<LegacyPatientLabOrderNewRedirect />} />
                </Route>
              </Route>

              <Route path="/leads" element={
                <OrgAppShell />
              }>
                <Route index element={<Leads />} />
                <Route
                  path="add"
                  element={<Navigate to="/leads" replace state={{ openAddLead: true }} />}
                />
                <Route path=":id" element={<LeadDetails />}>
                  <Route index element={null} />
                  <Route path="edit" element={<EditLead />} />
                </Route>
              </Route>

              <Route path="/messaging" element={
                <OrgAppShell />
              }>
                <Route index element={<MessagingInbox />} />
                <Route path="canned" element={<MessagingCannedResponses />} />
              </Route>

              <Route path="/internal-chat" element={
                <OrgAppShell />
              }>
                <Route index element={<InternalChatInbox />} />
              </Route>

              <Route path="/appointments" element={
                <OrgAppShell />
              }>
                <Route index element={<Appointments />} />
                <Route path=":id" element={<AppointmentDetailRedirect />} />
              </Route>

              <Route path="/appointment-types" element={
                <OrgAppShell />
              }>
                <Route index element={<AppointmentTypes />} />
              </Route>

              <Route path="/customer-sources" element={
                <OrgAppShell />
              }>
                <Route index element={<CustomerSources />} />
              </Route>

              <Route path="/diagnosis-codes" element={
                <OrgAppShell />
              }>
                <Route index element={<DiagnosisCodes />} />
              </Route>

              <Route path="/diagnosis-blocks" element={
                <OrgAppShell />
              }>
                <Route index element={<DiagnosisBlocks />} />
              </Route>

              <Route path="/treatment-categories" element={
                <OrgAppShell />
              }>
                <Route index element={<TreatmentCategories />} />
              </Route>

              <Route path="/treatment-uoms" element={
                <OrgAppShell />
              }>
                <Route index element={<TreatmentUoms />} />
              </Route>

              <Route path="/treatment-journals" element={
                <OrgAppShell />
              }>
                <Route index element={<TreatmentJournals />} />
              </Route>

              
              <Route path="/treatment-catalog" element={
                <OrgAppShell />
              }>
                <Route element={<TreatmentCatalog />}>
                  <Route index element={null} />
                  <Route path="add" element={<Navigate to="/treatment-catalog" replace />} />
                  <Route path=":id/edit" element={<EditTreatmentCatalogItem />} />
                  <Route path=":id" element={<TreatmentCatalogDetailRedirect />} />
                </Route>
              </Route>

              <Route path="/treatment-plans" element={
                <OrgAppShell />
              }>
                <Route index element={<TreatmentPlans />} />
                <Route path="add" element={<Navigate to="/patients" replace />} />
                <Route path=":id/edit" element={<EditTreatmentPlan />} />
                <Route path=":id" element={<TreatmentPlanDetails />} />
              </Route>

              <Route path="/treatment-sessions" element={
                <OrgAppShell />
              }>
                <Route index element={<TreatmentSessions />} />
                <Route path="add" element={<AddTreatmentSession />} />
                <Route path=":id/edit" element={<EditTreatmentSession />} />
                <Route path=":id" element={<TreatmentSessionDetailRedirect />} />
              </Route>

              {/* Legacy redirects */}
              <Route path="/treatments" element={<Navigate to="/treatment-catalog" replace />} />
              <Route path="/treatments/add" element={<Navigate to="/treatment-catalog" replace />} />
              <Route path="/treatments/plans" element={<Navigate to="/treatment-plans" replace />} />
              <Route path="/treatments/:id/edit" element={<LegacyTreatmentEditRedirect />} />
              <Route path="/treatments/:id" element={<LegacyTreatmentDetailRedirect />} />
              
              <Route path="/payments" element={
                <OrgAppShell />
              }>
                <Route index element={<Payments />} />
                <Route path="new" element={<Navigate to="/payments" replace />} />
                <Route path=":id/edit" element={<Navigate to="/payments" replace />} />
              </Route>

              <Route path="/expenses" element={
                <OrgAppShell />
              }>
                <Route index element={<Expenses />} />
              </Route>

              <Route path="/expense-categories" element={
                <OrgAppShell />
              }>
                <Route index element={<ExpenseCategories />} />
              </Route>

              <Route path="/payment-methods" element={
                <OrgAppShell />
              }>
                <Route index element={<PaymentMethods />} />
              </Route>

              <Route path="/suppliers">
                <Route index element={<Navigate to="/inventory?tab=suppliers" replace />} />
              </Route>

              <Route path="/incomes" element={
                <OrgAppShell />
              }>
                <Route index element={<Incomes />} />
              </Route>

              <Route path="/income-categories" element={
                <OrgAppShell />
              }>
                <Route index element={<IncomeCategories />} />
              </Route>

              <Route path="/promotions" element={<Navigate to="/marketing/promotions" replace />} />
              <Route path="/promotions/*" element={<Navigate to="/marketing/promotions" replace />} />

              <Route path="/marketing" element={<OrgAppShell />}>
                <Route index element={<Navigate to="referrers" replace />} />
                <Route path="referrers" element={<ReferrerHub />} />
                <Route path="referral-commissions" element={<ReferralCommissionHub />} />
                <Route path="promotions">
                  <Route index element={<Promotions />} />
                  <Route path="add" element={<Navigate to="/marketing/promotions" replace />} />
                  <Route path=":id/edit" element={<Navigate to="/marketing/promotions" replace />} />
                </Route>
              </Route>

              <Route path="/commissions" element={
                <OrgAppShell />
              }>
                <Route index element={<CommissionHub />} />
                <Route path="rules/new" element={<Navigate to="/commissions?tab=rules" replace />} />
                <Route path="rules/:id/edit" element={<CommissionRuleEditRedirect />} />
                <Route path="rules/:id" element={<CommissionRuleEditRedirect />} />
              </Route>

              <Route path="/payroll" element={<OrgAppShell />}>
                <Route index element={<PayrollHub />} />
                <Route path="structures" element={<Navigate to="/payroll?tab=formulas" replace />} />
                <Route path=":id" element={<PayrollPeriodDetails />} />
                <Route path=":id/entries/:entryId" element={<PayrollEntryPageRedirect />} />
              </Route>

              <Route path="/commission-rules">
                <Route index element={<Navigate to="/commissions?tab=rules" replace />} />
                <Route path="new" element={<Navigate to="/commissions?tab=rules" replace />} />
                <Route path=":id/edit" element={<CommissionRuleEditRedirect />} />
                <Route path=":id" element={<CommissionRuleEditRedirect />} />
              </Route>

              <Route path="/cashbook" element={
                <OrgAppShell />
              }>
                <Route index element={<Cashbook />} />
              </Route>
              
              <Route path="/inventory" element={
                <OrgAppShell />
              }>
                <Route path="report" element={<InventoryReports />} />
                <Route path="stocktakes">
                  <Route index element={<StocktakeList />} />
                  <Route path="create" element={<StocktakeIdRedirect />} />
                  <Route path=":id" element={<StocktakeIdRedirect />} />
                </Route>
                <Route path="transfers">
                  <Route index element={<TransferList />} />
                  <Route path=":id" element={<TransferIdRedirect />} />
                </Route>
                <Route path="stock-out">
                  <Route index element={<StockOutList />} />
                  <Route path="create" element={<LegacyStockOutCreateRedirect />} />
                  <Route path=":id" element={<StockOutIssueRedirect />} />
                </Route>
                <Route path="stock-in">
                  <Route index element={<StockInList />} />
                  <Route path=":id" element={<StockInReceiptRedirect />} />
                </Route>
                <Route index element={<WarehouseConfigHub />} />
              </Route>

              <Route path="/labo" element={
                <OrgAppShell />
              }>
                <Route path="master" element={<LaboMasterHub />} />
                <Route path="partners">
                  <Route index element={<Navigate to="/labo/master" replace />} />
                  <Route path="add" element={<Navigate to="/labo/master" replace />} />
                  <Route path=":id/edit" element={<LabPartnerEditRedirect />} />
                  <Route path=":id" element={<LaboMasterHub />} />
                </Route>
                <Route
                  path="catalog"
                  element={<Navigate to="/labo/master?tab=workTypes" replace />}
                />
                <Route
                  path="catalog/*"
                  element={<Navigate to="/labo/master?tab=workTypes" replace />}
                />
                <Route path="orders">
                  <Route index element={<LabOrders />} />
                  <Route path="create" element={<CreateLabOrder />} />
                  <Route path=":id" element={<LabOrderIdRedirect />} />
                </Route>
              </Route>
              
              <Route path="/drugs" element={
                <OrgAppShell />
              }>
                <Route index element={<Drugs />} />
              </Route>

              <Route path="/prescription-templates" element={
                <OrgAppShell />
              }>
                <Route index element={<PrescriptionTemplates />} />
              </Route>
              
              <Route path="/prescriptions" element={
                <OrgAppShell />
              }>
                <Route index element={<Prescriptions />} />
                <Route path="add" element={<Navigate to="/prescriptions" replace />} />
              </Route>
              
              <Route path="/clinics" element={
                <OrgAppShell />
              }>
                <Route path="add" element={<Navigate to="/clinics" replace state={{ openAddClinic: true }} />} />
                <Route element={<Clinics />}>
                  <Route index element={null} />
                  <Route path="edit/:id" element={null} />
                  <Route path=":id" element={null} />
                </Route>
              </Route>
              
              <Route path="/staff" element={
                <OrgAppShell />
              }>
                <Route index element={<Staff />} />
                <Route path="add" element={<AddStaff />} />
                <Route path="edit/:id" element={<EditStaff />} />
                <Route path=":id" element={<StaffDetails />} />
              </Route>

              <Route path="/departments" element={
                <OrgAppShell />
              }>
                <Route index element={<Departments />} />
              </Route>

              <Route path="/work-timesheet" element={
                <OrgAppShell />
              }>
                <Route index element={<WorkTimesheetPage />} />
              </Route>

              <Route path="/scheduling" element={
                <OrgAppShell />
              }>
                <Route index element={<TeamScheduleHub />} />
              </Route>


              <Route path="/scheduling/time-off" element={
                <OrgAppShell />
              }>
                <Route index element={<TimeOffHub />} />
                <Route
                  path="request"
                  element={<Navigate to="/scheduling/time-off?request=1" replace />}
                />
                <Route path=":id" element={<TimeOffDetailRedirect />} />
              </Route>

              <Route
                path="/scheduling/shift-types"
                element={<Navigate to="/scheduling?tab=shiftTypes" replace />}
              />

              <Route
                path="/scheduling/time-off-types"
                element={<Navigate to="/scheduling/time-off?tab=types" replace />}
              />
              <Route
                path="/scheduling/leave-entitlements"
                element={<Navigate to="/scheduling/time-off?tab=entitlements" replace />}
              />
              <Route
                path="/scheduling/leave-ledger"
                element={<Navigate to="/scheduling/time-off?tab=ledger" replace />}
              />
              <Route
                path="/scheduling/leave-report"
                element={<Navigate to="/scheduling/time-off?tab=report" replace />}
              />

              <Route
                path="/scheduling/weekly-templates"
                element={<Navigate to="/scheduling?tab=templates" replace />}
              />
              <Route
                path="/scheduling/weekly-templates/*"
                element={<Navigate to="/scheduling?tab=templates" replace />}
              />

              <Route path="/scheduling/assignments" element={
                <OrgAppShell />
              }>
                <Route index element={<Navigate to="/scheduling?tab=templates" replace />} />
                <Route path="apply" element={<ApplyWeeklyTemplateRedirect />} />
              </Route>

              <Route path="/me" element={
                <OrgAppShell />
              }>
                <Route index element={<MeHome />} />
                <Route path="attendance" element={<Navigate to="/profile?tab=attendance" replace />} />
                <Route path="attendance/clock" element={<Navigate to="/profile?tab=attendance" replace />} />
                <Route path="schedule" element={<Navigate to="/profile?tab=schedule" replace />} />
                <Route path="time-off" element={<Navigate to="/profile?tab=timeOff" replace />} />
                <Route
                  path="time-off/request"
                  element={<Navigate to="/profile?tab=timeOff&request=1" replace />}
                />
                <Route path="payslips" element={<Navigate to="/profile?tab=payslips" replace />} />
                <Route path="advances" element={<Navigate to="/profile?tab=advances" replace />} />
                <Route path="profile" element={<Navigate to="/profile" replace />} />
              </Route>

              <Route path="/attendance" element={
                <OrgAppShell />
              }>
                <Route index element={<AttendanceHub />} />
                <Route path="clock" element={<AttendanceClock />} />
                <Route
                  path="monthly"
                  element={<Navigate to="/attendance?view=month" replace />}
                />
                <Route
                  path="requests"
                  element={<Navigate to="/attendance?tab=requests" replace />}
                />
                <Route path=":id" element={<AttendanceDetails />} />
              </Route>

              <Route path="/overtime" element={
                <OrgAppShell />
              }>
                <Route index element={<OvertimeHub />} />
                <Route
                  path="requests"
                  element={<Navigate to="/overtime?tab=requests" replace />}
                />
              </Route>

              <Route path="/tasks" element={
                <OrgAppShell />
              }>
                <Route index element={<Tasks />} />
                <Route path="add" element={<AddTask />} />
                <Route path=":id/edit" element={<EditTask />} />
                <Route path=":id" element={<TaskDetails />} />
              </Route>

              <Route path="/projects" element={
                <OrgAppShell />
              }>
                <Route index element={<Projects />} />
                <Route path="add" element={<Navigate to="/projects?view=list&create=1" replace />} />
                <Route path=":id/edit" element={<ProjectLegacyRedirect edit />} />
                <Route path=":id" element={<ProjectLegacyRedirect />} />
              </Route>

              <Route path="/kpi" element={<OrgAppShell />}>
                <Route index element={<Navigate to="/kpi/cycles" replace />} />
                <Route path="cycles" element={<KpiHub />} />
                <Route path="cycles/add" element={<Navigate to="/kpi/cycles" replace />} />
                <Route path="cycles/:id" element={<KpiCycleDetails />} />
                <Route path="sheets" element={<Navigate to="/kpi/cycles?tab=sheets" replace />} />
                <Route path="sheets/add" element={<Navigate to="/kpi/cycles" replace />} />
                <Route path="sheets/:id" element={<LegacyKpiSheetRedirect />} />
                <Route path="metrics" element={<Navigate to="/kpi/cycles?tab=metrics" replace />} />
                <Route path="templates" element={<Navigate to="/kpi/cycles?tab=templates" replace />} />
                <Route path="me" element={<Navigate to="/kpi/cycles" replace />} />
              </Route>

              <Route path="/notifications" element={
                <OrgAppShell />
              }>
                <Route index element={<Notifications />} />
              </Route>

              <Route path="/users" element={
                <OrgAppShell />
              }>
                <Route index element={<Users />} />
              </Route>

              <Route path="/permission-groups" element={
                <OrgAppShell />
              }>
                <Route index element={<PermissionGroups />} />
              </Route>

              <Route path="/audit" element={
                <OrgAppShell />
              }>
                <Route index element={<AuditEvents />} />
              </Route>
              
              <Route path="/reports" element={
                <OrgAppShell />
              }>
                <Route index element={<Navigate to="revenue" replace />} />
                <Route path="overview" element={<Navigate to="/reports/revenue" replace />} />
                <Route path="revenue" element={<RevenueReport />} />
                <Route path="patients" element={<PatientsReport />} />
                <Route path="referrers" element={<ReferrersReport />} />
                <Route path="appointments" element={<AppointmentsReport />} />
                <Route path="treatments" element={<TreatmentsReport />} />
                <Route path="leads" element={<LeadReports />} />
                <Route path="inventory" element={<Navigate to="/inventory/report" replace />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              
              <Route path="/automation" element={
                <OrgAppShell />
              }>
                <Route index element={<Automation />} />
                <Route path="rules/new" element={<AutomationRuleEditor />} />
                <Route path="rules/:id" element={<AutomationRuleEditor />} />
              </Route>

              <Route path="/outbound" element={
                <OrgAppShell />
              }>
                <Route index element={<OutboundChannels />} />
              </Route>

              <Route path="/settings" element={
                <OrgAppShell />
              }>
                <Route index element={<Settings />} />
                <Route
                  path="commission-rules/new"
                  element={<Navigate to="/commissions?tab=rules" replace />}
                />
                <Route
                  path="commission-rules/:id/edit"
                  element={<CommissionRuleEditRedirect />}
                />
                <Route
                  path="commission-rules/:id"
                  element={<CommissionRuleEditRedirect />}
                />
              </Route>
              
              <Route path="/profile" element={
                <OrgAppShell />
              }>
                <Route index element={<Profile />} />
              </Route>

              <Route path="*" element={<NotFound />} />
      </Route>
    </Route>
  )
);

function App(): React.JSX.Element {
  // Default startTransition updates the URL before React commits the new route,
  // so the previous screen stays up until the lazy chunk and first render finish.
  return <RouterProvider router={router} useTransitions={false} />;
}

export default App;
